import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";
import { card, input, label, btnPrimary, btnGhost } from "./ui";
import { Loader2, ArrowLeft, Save, Eye, EyeOff, Trash2, ExternalLink, Upload, Plus, X } from "lucide-react";

const empty = { type: "artigo", title: "", slug: "", excerpt: "", body: "", meta_title: "", meta_description: "", keyword: "", cta: "", faq: [] };

export default function SeoEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(() => (location.state?.draft ? { ...empty, ...location.state.draft } : empty));
  const [status, setStatus] = useState("rascunho");
  const [cover, setCover] = useState("");
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isNew = !id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!id) return;
    api.get(`/contents/${id}`)
      .then(({ data }) => {
        setForm({ type: data.type, title: data.title, slug: data.slug, excerpt: data.excerpt || "",
                  body: data.body || "", meta_title: data.meta_title || "", meta_description: data.meta_description || "",
                  keyword: data.keyword || "", cta: data.cta || "", faq: data.faq || [] });
        setStatus(data.status);
        setCover(data.cover_image || "");
      })
      .catch(() => { toast.error("Conteúdo não encontrado."); navigate("/dashboard/seo"); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const save = async () => {
    if (form.title.trim().length < 3) return toast.error("Informe um título.");
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/contents", form);
        toast.success("Rascunho salvo.");
        navigate(`/dashboard/seo/${data.id}`, { replace: true });
      } else {
        await api.put(`/contents/${id}`, form);
        toast.success("Alterações salvas.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggleStatus = async () => {
    const next = status === "publicado" ? "rascunho" : "publicado";
    try {
      const { data } = await api.post(`/contents/${id}/status`, { status: next });
      setStatus(data.status);
      toast.success(next === "publicado" ? "Publicado! Já está no ar e no sitemap." : "Despublicado — saiu do ar e do sitemap.");
    } catch { toast.error("Não consegui alterar o status."); }
  };

  const del = async () => {
    if (!window.confirm("Excluir este conteúdo definitivamente?")) return;
    try {
      await api.delete(`/contents/${id}`);
      toast.success("Conteúdo excluído.");
      navigate("/dashboard/seo");
    } catch { toast.error("Não consegui excluir."); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/contents/${id}/cover`, fd);
      setCover(data.cover_image);
      toast.success("Imagem de capa enviada.");
    } catch { toast.error("Não consegui enviar a imagem."); }
    finally { setUploading(false); }
  };

  const setFaq = (i, k, v) => setForm((f) => ({ ...f, faq: f.faq.map((item, j) => (j === i ? { ...item, [k]: v } : item)) }));
  const publicUrl = form.type === "artigo" ? `/conteudos/${form.slug}` : `/${form.slug}`;

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#5B6E5B]" />;

  return (
    <div data-testid="seo-edit" className="max-w-3xl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/dashboard/seo")} data-testid="seo-back"
            className="grid place-items-center w-9 h-9 rounded-full border border-[#5B6E5B]/25 text-[#5B6E5B] hover:bg-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <p className="font-body text-xs tracking-[0.3em] uppercase text-[#C87A5E]">{isNew ? "Novo conteúdo" : "Editar conteúdo"}</p>
            <h1 className="font-display text-2xl text-[#2C332A]">{form.title || "Sem título"}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && status === "publicado" && (
            <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="seo-open-public"
              className={`${btnGhost} inline-flex items-center gap-2`}>
              <ExternalLink className="w-4 h-4" /> Ver página
            </a>
          )}
          {!isNew && (
            <button onClick={toggleStatus} data-testid="seo-status-toggle"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm transition-colors ${status === "publicado" ? "border border-[#5B6E5B]/25 text-[#5B6E5B] hover:bg-white" : "bg-[#5B6E5B] text-[#FDFBF7] hover:bg-[#4A5D4E]"}`}>
              {status === "publicado" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {status === "publicado" ? "Despublicar" : "Publicar"}
            </button>
          )}
          {!isNew && (
            <button onClick={del} data-testid="seo-delete-btn"
              className="grid place-items-center w-10 h-10 rounded-full text-[#C87A5E] hover:bg-[#C87A5E]/10 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className={`${card} p-6 sm:p-8 space-y-5`}>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={label}>Tipo</label>
            <select className={input} value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="seo-field-type">
              <option value="pagina">Página SEO (URL na raiz)</option>
              <option value="artigo">Artigo (em /conteudos/)</option>
            </select>
          </div>
          <div>
            <label className={label}>Slug / URL</label>
            <input className={input} value={form.slug} onChange={(e) => set("slug", e.target.value)}
              placeholder="gerado a partir do título" data-testid="seo-field-slug" />
          </div>
        </div>
        <div>
          <label className={label}>Título (H1)</label>
          <input className={input} value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="seo-field-title" />
        </div>
        <div>
          <label className={label}>Resumo</label>
          <textarea rows={2} className={`${input} resize-none`} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="seo-field-excerpt" />
        </div>
        <div>
          <label className={label}>Conteúdo (Markdown: ## subtítulo, ### seção, **negrito**, - lista)</label>
          <textarea rows={16} className={`${input} font-mono text-sm leading-relaxed`} value={form.body}
            onChange={(e) => set("body", e.target.value)} data-testid="seo-field-body" />
        </div>
      </div>

      <div className={`${card} p-6 sm:p-8 space-y-5 mt-6`}>
        <h2 className="font-display text-xl text-[#2C332A]">SEO</h2>
        <div>
          <label className={label}>Meta title ({form.meta_title.length}/60)</label>
          <input className={input} value={form.meta_title} onChange={(e) => set("meta_title", e.target.value)} data-testid="seo-field-meta-title" />
        </div>
        <div>
          <label className={label}>Meta description ({form.meta_description.length}/160)</label>
          <textarea rows={2} className={`${input} resize-none`} value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} data-testid="seo-field-meta-desc" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={label}>Palavra-chave / tema principal</label>
            <input className={input} value={form.keyword} onChange={(e) => set("keyword", e.target.value)} data-testid="seo-field-keyword" />
          </div>
          <div>
            <label className={label}>Chamada para agendamento (CTA)</label>
            <input className={input} value={form.cta} onChange={(e) => set("cta", e.target.value)} data-testid="seo-field-cta" />
          </div>
        </div>
        {!isNew && (
          <div>
            <label className={label}>Imagem de capa (opcional)</label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadCover} />
            <div className="flex items-center gap-3 flex-wrap">
              {cover && <img src={`${API}/files/${cover}`} alt="Capa" className="h-16 rounded-xl border border-[#5B6E5B]/15" data-testid="seo-cover-preview" />}
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="seo-cover-upload"
                className={`${btnGhost} inline-flex items-center gap-2`}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {cover ? "Trocar imagem" : "Enviar imagem"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={`${card} p-6 sm:p-8 space-y-4 mt-6`}>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl text-[#2C332A]">Perguntas frequentes</h2>
          <button type="button" onClick={() => setForm((f) => ({ ...f, faq: [...f.faq, { q: "", a: "" }] }))} data-testid="seo-faq-add"
            className={`${btnGhost} inline-flex items-center gap-1.5 text-xs`}>
            <Plus className="w-3.5 h-3.5" /> Adicionar
          </button>
        </div>
        {form.faq.length === 0 && <p className="font-body text-sm text-[#7A8A7A]">Nenhuma pergunta. A IA costuma sugerir algumas.</p>}
        {form.faq.map((f, i) => (
          <div key={i} className="rounded-xl border border-[#5B6E5B]/15 p-4 space-y-2" data-testid="seo-faq-item">
            <div className="flex items-center gap-2">
              <input className={input} value={f.q} onChange={(e) => setFaq(i, "q", e.target.value)} placeholder="Pergunta" data-testid="seo-faq-q" />
              <button type="button" onClick={() => setForm((ff) => ({ ...ff, faq: ff.faq.filter((_, j) => j !== i) }))}
                className="shrink-0 grid place-items-center w-9 h-9 rounded-full text-[#C87A5E] hover:bg-[#C87A5E]/10">
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea rows={2} className={`${input} resize-none`} value={f.a} onChange={(e) => setFaq(i, "a", e.target.value)} placeholder="Resposta" data-testid="seo-faq-a" />
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button onClick={save} disabled={saving} data-testid="seo-save"
          className={`${btnPrimary} inline-flex items-center gap-2`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isNew ? "Salvar rascunho" : "Salvar alterações"}
        </button>
      </div>
    </div>
  );
}
